import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { WifiOff, AlertTriangle, RefreshCw, Check } from 'lucide-react';

const ALERT_RECIPIENTS = ['admin@biotop.com'];

export const DisconnectionAlert = () => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [visible, setVisible] = useState(false);
  const [acknowledging, setAcknowledging] = useState(null);

  const isRecipient = user?.email && ALERT_RECIPIENTS.includes(user.email);

  const checkAlerts = useCallback(async () => {
    if (!isRecipient) return;
    try {
      const res = await api.getDisconnectionAlerts();
      const activeAlerts = res.data.alerts || [];
      setAlerts(activeAlerts);
      setVisible(activeAlerts.length > 0);
    } catch (e) {}
  }, [isRecipient]);

  useEffect(() => {
    if (!isRecipient) return;
    checkAlerts();
    const interval = setInterval(checkAlerts, 30000);
    return () => clearInterval(interval);
  }, [isRecipient, checkAlerts]);

  const handleAcknowledge = async (alertId) => {
    setAcknowledging(alertId);
    try {
      await api.acknowledgeDisconnectionAlert(alertId);
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      if (alerts.length <= 1) {
        setVisible(false);
      }
    } catch (e) {}
    setAcknowledging(null);
  };

  const handleAcknowledgeAll = async () => {
    setAcknowledging('all');
    try {
      await Promise.all(alerts.map(a => api.acknowledgeDisconnectionAlert(a.id)));
      setAlerts([]);
      setVisible(false);
    } catch (e) {}
    setAcknowledging(null);
  };

  if (!isRecipient || !visible || alerts.length === 0) return null;

  return (
    <div
      className="fixed top-4 right-4 z-50 max-w-md w-[90vw] sm:w-[420px]"
      data-testid="disconnection-alert-overlay"
    >
      <div
        className="rounded-2xl overflow-hidden shadow-2xl border border-red-200 bg-white animate-in slide-in-from-top-2 duration-300"
        data-testid="disconnection-alert-modal"
      >
        {/* Header */}
        <div className="bg-red-600 px-4 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center shrink-0">
            <WifiOff className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-bold text-white tracking-tight">
              WhatsApp Desconectado
            </h1>
            <p className="text-red-100 text-xs">
              {alerts.length === 1
                ? '1 instancia precisa reconexao'
                : `${alerts.length} instancias precisam reconexao`}
            </p>
          </div>
          <button
            onClick={handleAcknowledgeAll}
            disabled={acknowledging === 'all'}
            className="text-white/80 hover:text-white text-xs p-1"
            title="Fechar todos os alertas"
            data-testid="acknowledge-all-alerts-btn"
          >
            {acknowledging === 'all' ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Body */}
        <div className="bg-white p-3">
          <div className="flex items-start gap-2 mb-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 leading-snug">
              Mensagens recebidas nao serao registradas. Reconecte em <strong>Instancias</strong>.
            </p>
          </div>

          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {alerts.map(alert => (
              <div
                key={alert.id}
                className="flex items-center justify-between p-2 bg-red-50 border border-red-200 rounded-lg"
                data-testid={`disconnection-alert-item-${alert.instance_name}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 text-sm truncate">
                      {alert.instance_name}
                    </p>
                    {alert.detected_at && (
                      <p className="text-[11px] text-slate-500 truncate">
                        Desde {new Date(alert.detected_at).toLocaleString('pt-BR')}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleAcknowledge(alert.id)}
                  disabled={acknowledging === alert.id || acknowledging === 'all'}
                  className="shrink-0 px-2 py-1 text-[11px] font-medium bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition-colors disabled:opacity-50"
                  data-testid={`acknowledge-alert-${alert.instance_name}`}
                >
                  {acknowledging === alert.id ? (
                    <RefreshCw className="w-3 h-3 animate-spin" />
                  ) : (
                    'Ciente'
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
